import { Button } from "@component/button";
import { getApplicantDisplayCV, getCVById } from "@service/cv";
import { ApplicantCVDisplay, CandidateCV } from "@type/cv";
import { parseFormat } from "@util/date";
import {
    ArrowLeftIcon, Bookmark, Building2,
    Download, FileIcon, Share2, X
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { deleteShortlistCandidate, getJobDetail, getListJobApplicant, inviteToApply } from "@service/job";
import { cn } from "@util/tailwind";
import InviteConfirmationDialogV2 from "@component/invite-confirmation-v2-dialog";
import { catchErrorLog } from "@util/sentry";
import {
    bookmarkingCandidate, getShortlistedCounts,
    removeBookmarkCandidate, searchShortlistedCandidate
} from "@service/user";
import { getCookie, setCookie } from "@util/cookie";
import { useUserProfileContext } from "@context/user_profile";
import Toast from "@component/toast";
import {
    CvData, SearchShortlistedCandidateRequest, ShortlistedCandidateItem,
    ShortlistedCounts, ShortlistedSearchType
} from "@type/candidate";
import { InviteToApplyRequest, Job, JobApplicantItem } from "@type/job";
import { MetaPagination } from "@type/common";
import { defaultPagination } from "@constant/app";
import { PDFViewer } from "@component/pdf-viewer";
import JobApplicationsList from "@container/job-applications-list";
import ProfileTabContent from "@container/drawer/tabs/ProfileTabContent";
import ReviewTabContent from "@container/drawer/tabs/ReviewTabContent";
import ShortlistToInviteDialog from "@container/dialog/dialog-shortlist-invite";
import { useSnackbar } from "@hook/use-snackbar";

export default function CandidateProfile() {
    const params = useParams()
    const [searchParams] = useSearchParams();
    // This page is the drawer's "Share profile" link target — the URL carries
    // `?job_id=` so a shared candidate opens in the same job context the drawer
    // was in. Scope the Review tab to that job when present.
    const jobId = searchParams.get("job_id") || "";
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const [isLoadingUserInfo, currentUserInfo] = useUserProfileContext();
    const [activeTab, setActiveTab] = useState<string>("profile");
    const [isLoading, setLoading] = useState<boolean>(true);
    const [candidate, setCandidate] = useState<CandidateCV | undefined>(undefined)
    const [job, setJob] = useState<Job | null>(null);
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [isLoadingInvitation, setLoadingInvitation] = useState<boolean>(false);
    const [isSuccessSentInvitation, setSuccessInvitation] = useState<boolean>(false);
    const [jobApplicantItems, setJobApplicantItems] = useState<JobApplicantItem[]>([]);
    const [isSaved, setIsSaved] = useState<boolean>(false);
    const [toastMessage, setToastMessage] = useState<string>("");
    const [toastVisible, setToastVisible] = useState<boolean>(false);
    const contentRef = useRef<HTMLDivElement>(null);

    // Profile tab — section navigation state (mirrors the drawer).
    const [activeSection, setActiveSection] = useState<string>("");
    const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({
        personal: null,
        skills: null,
        experience: null,
        education: null,
        awards: null,
        certifications: null,
        projects: null,
    });

    // CV tab — the parsed CV document (PDF + metadata).
    const [cvDisplay, setCvDisplay] = useState<ApplicantCVDisplay | null>(null);

    // Review tab — shortlist favourites + comments, same shape as the drawer.
    const [shortlistDialogOpen, setShortlistDialogOpen] = useState<boolean>(false);
    const [shortlistedCandidates, setShortlistedCandidates] = useState<ShortlistedCandidateItem[]>([]);
    const [isLoadingShortlistedCandidate, setLoadingShortlistedCandidate] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [reviewType, setReviewType] = useState<ShortlistedSearchType>("all");
    const [reviewCounts, setReviewCounts] = useState<ShortlistedCounts>({
        all: 0,
        favorites: 0,
        comments: 0,
    });
    const [metaPagination, setMetaPagination] = useState<MetaPagination>({
        limit: 0,
        page: 1,
        total_pages: 0,
        total: 0,
    });

    const candidateId = candidate?.user.id || "";

    const fetchCandidateByID = async (candidate_id: string) => {
        try {
            const resp = await getCVById(candidate_id)
            if (resp) {
                setCandidate(resp);
            }
        } catch (error) {
            console.error("failed to get candidate", error);
            catchErrorLog(`failed to get candidate: ${error instanceof Error ? error.message : error}`)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (params.candidateid) {
            fetchCandidateByID(params.candidateid)
        }
    }, [params.candidateid])

    // Resolve the job context from the shared link's ?job_id= so the Review tab
    // is scoped to this applicant-in-job rather than the candidate globally.
    useEffect(() => {
        if (!jobId) {
            setJob(null);
            return;
        }
        getJobDetail(jobId)
            .then((resp) => setJob(resp))
            .catch((error) => {
                catchErrorLog(`failed to get job detail on candidate page: ${error}`);
                setJob(null);
            });
    }, [jobId])

    useEffect(() => {
        if (!isLoadingUserInfo && currentUserInfo && candidate) {
            const savedCandidates = getCookie(`saved_candidate:${currentUserInfo?.id}`);
            if (savedCandidates) {
                const exists = savedCandidates.find((item: string) => item === candidate.user.id);
                setIsSaved(!!exists);
            } else {
                setIsSaved(false);
            }
        }
    }, [isLoadingUserInfo, currentUserInfo, candidate])

    useEffect(() => {
        if (candidate) {
            getListJobApplicant(candidate.user.id).then((resp) => {
                if (resp.status === 200) {
                    setJobApplicantItems(resp.data.data);
                }
            })
        }
    }, [candidate])

    // CV tab — fetch the parsed CV document lazily when the tab is opened.
    useEffect(() => {
        if (candidateId && activeTab === "cv") {
            getApplicantDisplayCV(candidateId).then((resp) => {
                if (resp.status === 200) {
                    setCvDisplay(resp.data.data);
                }
            }).catch((error) => {
                catchErrorLog(`failed to get candidate CV display: ${error}`);
            })
        }
    }, [candidateId, activeTab])

    // Review tab — the chip-badge counts (all / favourites / comments),
    // independent of the active `type` filter so the badges stay correct.
    const refreshReviewCounts = () => {
        if (!candidateId) return;
        getShortlistedCounts({
            candidate_id: candidateId,
            view: job ? "applicant" : "candidate",
            job_id: job ? job.id : undefined,
        })
            .then((resp) => {
                if (resp.status === 200 && resp.data?.data) {
                    setReviewCounts(resp.data.data);
                }
            })
            .catch(() => {
                // Silent — chip badges just keep their last known values.
            });
    };

    useEffect(() => {
        if (activeTab !== "review" || !candidateId) return;
        setShortlistedCandidates([]);
        setLoadingShortlistedCandidate(true);
        const params: SearchShortlistedCandidateRequest = {
            page: currentPage,
            limit: defaultPagination.limit,
            candidate_id: candidateId,
            view: job ? "applicant" : "candidate",
            job_id: job ? job.id : undefined,
            type: reviewType,
        };
        searchShortlistedCandidate(params).then((resp) => {
            if (resp.status === 200) {
                setShortlistedCandidates(resp.data.data);
                setMetaPagination(resp.data.meta.pagination);
            }
        }).finally(() => {
            setLoadingShortlistedCandidate(false);
        });
        refreshReviewCounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, candidateId, job, currentPage, reviewType])

    // Scroll to the profile section chosen in the sub-nav.
    useEffect(() => {
        if (
            activeTab === "profile" &&
            activeSection &&
            sectionRefs.current[activeSection]
        ) {
            const sectionElement = sectionRefs.current[activeSection];
            if (sectionElement) {
                // Unlike the drawer (a fixed-height panel that scrolls its own
                // `contentRef`), this standalone page scrolls on the window — so
                // scroll the window to the section. The -100 offset clears the
                // sticky section sub-nav (and the mobile header).
                const top = sectionElement.getBoundingClientRect().top + window.scrollY - 100;
                window.scrollTo({ top, behavior: "smooth" });
            }
        }
    }, [activeSection, activeTab]);

    // Reset section highlight when leaving/entering the profile tab.
    useEffect(() => {
        if (activeTab === "profile") {
            setActiveSection("");
        }
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
    }, [activeTab]);

    const scrollToSection = (section: string) => {
        setActiveSection(section);
    };

    const reloadShortlist = () => {
        if (!candidateId) return;
        const params: SearchShortlistedCandidateRequest = {
            page: currentPage,
            limit: defaultPagination.limit,
            candidate_id: candidateId,
            view: job ? "applicant" : "candidate",
            job_id: job ? job.id : undefined,
            type: reviewType,
        };
        searchShortlistedCandidate(params).then((resp) => {
            if (resp.status === 200) {
                setShortlistedCandidates(resp.data.data);
                setMetaPagination(resp.data.meta.pagination);
            }
        });
        refreshReviewCounts();
    };

    const handleInviteConfirm = async (data: InviteToApplyRequest) => {
        setLoadingInvitation(true)
        try {
            const resp = await inviteToApply(data)
            if (resp.code === 201) {
                setSuccessInvitation(true);
                snackbar.showSuccess(resp.message || "Invitation sent successfully.");
            } else {
                snackbar.showError(resp.message || "Failed to send invitation.");
            }

            setLoadingInvitation(false)
        } catch (error) {
            if (error instanceof Error) {
                console.error("failed to invite to apply", error.message);
            } else {
                console.error("failed to invite to apply", error);
            }
            setLoadingInvitation(false)
        }
    };

    const handleBookmark = () => {
        setIsSaved(!isSaved);

        if (!isSaved) {
            bookmarkingCandidate(candidate?.user.id || "")
                .then((resp) => {
                    if (resp.status === 200) {
                        setCookie(`saved_candidate:${currentUserInfo?.id}`, JSON.stringify(resp.data.data));
                    }
                }).catch((error) => {
                    catchErrorLog(`failed to bookmark candidate: ${error}`);
                })
        } else {
            removeBookmarkCandidate(candidate?.user.id || "")
                .then((resp) => {
                    if (resp.status === 200) {
                        setCookie(`saved_candidate:${currentUserInfo?.id}`, JSON.stringify(resp.data.data));
                    }
                }).catch((error) => {
                    catchErrorLog(`failed to remove bookmark candidate: ${error}`);
                })
        }
    }

    const handleShare = () => {
        setToastMessage("");
        const reviewerLink = `${window.location.origin}/candidate/${params.candidateid}?w=${currentUserInfo?.company_id || ""}`;
        navigator.clipboard
            .writeText(reviewerLink)
            .then(() => {
                setToastVisible(true);
                setToastMessage("Reviewer link copied to clipboard!");
            })
            .catch((err) => {
                console.error("Could not copy text: ", err);
            });
    };

    const handleDownloadCV = async () => {
        if (!cvDisplay?.cv_url || !cvDisplay?.filename) return;

        try {
            const response = await fetch(cvDisplay.cv_url);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = cvDisplay.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download CV:', error);
            window.open(cvDisplay.cv_url, "_blank");
        }
    };

    // ProfileTabContent reads `candidate.CV_data.*`; the page's fetch returns the
    // raw CV under `candidate.cv.CV_data`. Adapt the shape for the shared component.
    const profileCandidate = { CV_data: candidate?.cv?.CV_data } as unknown as CvData;

    if (isLoading) {
        return (
            <>
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
                </div>
            </>
        );
    }

    const tabClass = (tab: string) => cn(
        "py-2 px-4 text-sm font-medium relative",
        activeTab === tab
            ? "text-blue-600 dark:text-blue-400"
            : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
    );

    return (
        <>
            <div className="bg-white dark:bg-gray-900">
                <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-4">
                    <button
                        onClick={() => navigate("/dashboard")}
                        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                    >
                        <ArrowLeftIcon size={16} />
                        Back to Dashboard
                    </button>
                    <div className="flex items-center space-x-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleBookmark}
                            className="h-8 w-8"
                            title={isSaved ? "Remove from bookmarks" : "Add to bookmarks"}
                        >
                            <Bookmark
                                className={cn(
                                    "h-4 w-4",
                                    isSaved && "fill-current text-blue-600 dark:text-blue-400"
                                )}
                            />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleShare}
                            className="h-8 w-8"
                            title="Share profile"
                        >
                            <Share2 className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate("/dashboard")}
                            className="h-8 w-8"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                {/* Main content area */}
                <div className="overflow-y-auto" ref={contentRef}>
                    {/* Candidate header with avatar and name */}
                    <div className="p-6 pb-0">
                        <div className="flex items-center space-x-4">
                            <div className="h-16 w-16 rounded-full overflow-hidden">
                                <img
                                    onError={(e) => {
                                        e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/default_avatar.png`;
                                        e.currentTarget.onerror = null; // Prevent infinite loop
                                    }}
                                    src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${candidate?.user.profile_picture_path}`}
                                    className="h-full w-full object-cover"
                                />
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold">{candidate?.cv?.CV_data.name}</h3>
                                <div className="flex items-center text-gray-500 dark:text-gray-400">
                                    <Building2 className="h-4 w-4 mr-1" />
                                    <span>{candidate?.cv?.CV_data.position}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tab navigation */}
                    <div className="border-b border-gray-200 dark:border-gray-800 mt-6">
                        <div className="flex px-6">
                            <button onClick={() => setActiveTab("profile")} className={tabClass("profile")}>
                                Profile
                                {activeTab === "profile" && (
                                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400"></div>
                                )}
                            </button>
                            <button onClick={() => setActiveTab("cv")} className={tabClass("cv")}>
                                CV
                                {activeTab === "cv" && (
                                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400"></div>
                                )}
                            </button>
                            <button onClick={() => setActiveTab("review")} className={tabClass("review")}>
                                Review
                                {activeTab === "review" && (
                                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400"></div>
                                )}
                            </button>
                            <button onClick={() => setActiveTab("job-applications")} className={tabClass("job-applications")}>
                                Job Applications
                                {activeTab === "job-applications" && (
                                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400"></div>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Tab content */}
                    <div className="py-6">
                        {activeTab === "profile" ? (
                            candidate?.cv?.CV_data ? (
                                <ProfileTabContent
                                    candidate={profileCandidate}
                                    activeSection={activeSection}
                                    scrollToSection={scrollToSection}
                                    sectionRefs={sectionRefs}
                                />
                            ) : null
                        ) : activeTab === "cv" ? (
                            <div className="h-full space-y-4 px-6">
                                {/* CV Details */}
                                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                                            <FileIcon className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
                                            CV Details
                                        </h4>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={!cvDisplay?.filename}
                                            onClick={handleDownloadCV}
                                            className="h-8 gap-2"
                                        >
                                            <Download className="h-4 w-4" />
                                            Download CV
                                        </Button>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="text-sm">
                                            <span className="text-gray-600 dark:text-gray-400">File Name:</span>
                                            <span className="text-gray-900 dark:text-gray-100 font-medium ml-2">
                                                {cvDisplay?.filename || ""}
                                            </span>
                                        </div>
                                        <div className="text-sm">
                                            <span className="text-gray-600 dark:text-gray-400">Date Parsed:</span>
                                            <span className="text-gray-900 dark:text-gray-100 font-medium ml-2">
                                                {cvDisplay?.uploaded_at ? parseFormat(cvDisplay.uploaded_at, "D MMMM YYYY") : ""}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* CV Preview */}
                                {cvDisplay && (
                                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg h-[600px]">
                                        <PDFViewer
                                            url={cvDisplay?.cv_url || ""}
                                            className="w-full h-full"
                                        />
                                    </div>
                                )}
                            </div>
                        ) : activeTab === "review" ? (
                            <div className="px-6">
                                <ReviewTabContent
                                    candidateId={candidateId}
                                    currentUserId={currentUserInfo?.id}
                                    currentUserName={[currentUserInfo?.first_name, currentUserInfo?.last_name].filter(Boolean).join(" ").trim() || undefined}
                                    currentUserPhoto={currentUserInfo?.profile_picture_path}
                                    job={job}
                                    isLoading={isLoadingShortlistedCandidate}
                                    shortlistedCandidates={shortlistedCandidates}
                                    metaPagination={metaPagination}
                                    currentPage={currentPage}
                                    reviewType={reviewType}
                                    reviewCounts={reviewCounts}
                                    onReviewTypeChange={setReviewType}
                                    onSetCurrentPage={setCurrentPage}
                                    onMarkAsFavourite={() => setShortlistDialogOpen(true)}
                                    onReload={reloadShortlist}
                                    onDeleteReview={(shortlistId) => {
                                        deleteShortlistCandidate(shortlistId).then(() => {
                                            snackbar.showSuccess("Favourite note deleted for candidate.");
                                            reloadShortlist();
                                        }).catch((err) => {
                                            catchErrorLog(`Failed to delete shortlist: ${err}`);
                                        });
                                    }}
                                />
                            </div>
                        ) : (
                            <div className="p-1">
                                <JobApplicationsList user_id={candidate?.user.id || ""} jobApplicantItems={jobApplicantItems} />
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer with action button */}
                <div className="py-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
                    <Button
                        disabled={isLoadingInvitation || isSuccessSentInvitation}
                        className={`flex-1 px-4 py-2 w-full`}
                        onClick={() => setInviteDialogOpen(true)}
                    >
                        {isLoadingInvitation ? "Sending..." : isSuccessSentInvitation ? "Invitation Sent" : "Invite to Apply"}
                    </Button>
                </div>
            </div>

            <InviteConfirmationDialogV2
                isOpen={inviteDialogOpen}
                onClose={setInviteDialogOpen}
                onConfirm={handleInviteConfirm}
                candidateName={candidate?.cv?.CV_data?.name || ""}
                candidate_id={candidate?.user.id || ""}
            />

            <ShortlistToInviteDialog
                candidate_id={candidateId}
                candidate_name={candidate?.cv?.CV_data.name || ""}
                isOpen={shortlistDialogOpen}
                selectedJob={job}
                onClose={() => {
                    setShortlistDialogOpen(false);
                    reloadShortlist();
                }}
            />

            <Toast
                message={toastMessage}
                visible={toastVisible}
                onClose={() => setToastVisible(false)}
            />
        </>
    )
}
